// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Modal, Form, Button, Icon, Message } from 'semantic-ui-react';
import { supportStaffApi } from '../api/supportStaffApi';
import type { SupportStaffDetail, SupportStaffUpdate } from '../types/supportStaff';

interface EditSupportStaffModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  supportStaff: SupportStaffDetail;
}

interface FormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  notes: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
}

function EditSupportStaffModal({
  open,
  onClose,
  onSuccess,
  supportStaff,
}: Readonly<EditSupportStaffModalProps>) {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    notes: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Populate form when modal opens
  useEffect(() => {
    if (open && supportStaff) {
      setFormData({
        firstName: supportStaff.firstName || '',
        lastName: supportStaff.lastName || '',
        phoneNumber: supportStaff.phoneNumber || '',
        notes: supportStaff.notes || '',
      });
      setErrors({});
      setSubmitError(null);
    }
  }, [open, supportStaff]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.length > 100) {
      newErrors.firstName = 'First name must be 100 characters or less';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.length > 100) {
      newErrors.lastName = 'Last name must be 100 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const update: SupportStaffUpdate = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phoneNumber: formData.phoneNumber.trim() || undefined,
        notes: formData.notes.trim() || undefined,
      };

      await supportStaffApi.update(supportStaff.id, update);
      onSuccess();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to update support staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const hasChanges = (): boolean => {
    return (
      formData.firstName.trim() !== (supportStaff.firstName || '') ||
      formData.lastName.trim() !== (supportStaff.lastName || '') ||
      formData.phoneNumber.trim() !== (supportStaff.phoneNumber || '') ||
      formData.notes.trim() !== (supportStaff.notes || '')
    );
  };

  return (
    <Modal open={open} onClose={onClose} size="small" closeIcon>
      <Modal.Header>
        <Icon name="edit" /> Edit Support Staff
      </Modal.Header>
      <Modal.Content>
        {submitError && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{submitError}</p>
          </Message>
        )}

        <Form>
          <Form.Group widths="equal">
            <Form.Input
              label="First Name"
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              error={errors.firstName ? { content: errors.firstName } : undefined}
              required
            />
            <Form.Input
              label="Last Name"
              placeholder="Enter last name"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              error={errors.lastName ? { content: errors.lastName } : undefined}
              required
            />
          </Form.Group>

          <Form.Input
            label="Email Address"
            value={supportStaff.email}
            disabled
            icon="lock"
            iconPosition="left"
          />
          <small style={{ color: '#666', display: 'block', marginTop: '-0.5em', marginBottom: '1em' }}>
            Email address cannot be changed.
          </small>

          <Form.Input
            label="Phone Number"
            placeholder="Enter phone number (optional)"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
          />

          <Form.TextArea
            label="Notes (Internal)"
            placeholder="Add internal notes about this support staff member..."
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={3}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSubmit}
          loading={submitting}
          disabled={!hasChanges()}
        >
          <Icon name="save" /> Save Changes
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default EditSupportStaffModal;
